import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CheckCircle2, Crown, Tag, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const INCLUDES = [
  "Complete California P&C digital workbook",
  "Full streaming audio library with resume",
  "Unlimited quiz bank with explanations",
  "Progress tracking & study dashboard",
  "Full access, billed annually",
];

export default function Upgrade() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [productId, setProductId] = useState("full_access");
  const [products, setProducts] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (user?.pending_coupon) {
      setCoupon(user.pending_coupon);
      applyCoupon(user.pending_coupon);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.pending_coupon]);

  const product = products.find((p) => p.id === productId);
  const base = product?.amount ?? 0;
  const finalAmount = applied ? applied.final_amount : base;

  const applyCoupon = async (codeArg) => {
    const c = (typeof codeArg === "string" ? codeArg : coupon).trim();
    if (!c) return;
    setChecking(true);
    try {
      const { data } = await api.post("/payments/validate-coupon", { code: c, product_id: productId });
      setApplied(data);
      if (data.free_access) toast.success("Free access code applied — no payment needed");
      else toast.success(`Code applied — you save $${data.discount.toFixed(2)}`);
      if (data.already_assigned_to) toast.info(`This code has already been assigned to ${data.already_assigned_to}`);
    } catch (e) {
      setApplied(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setChecking(false); }
  };

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        product_id: productId,
        coupon_code: applied ? coupon.trim() : null,
        origin_url: window.location.origin,
      });
      if (data.free_access) {
        await refresh();
        toast.success("Full access unlocked!");
        navigate("/dashboard");
        return;
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <Layout showFooter={false}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center mb-8">
          <div className="h-12 w-12 rounded-xl bg-amber-600 flex items-center justify-center mx-auto mb-4"><Crown className="h-6 w-6 text-white" /></div>
          <h1 className="font-heading text-3xl font-bold text-[#0A192F]">Unlock Full Access</h1>
          <p className="text-slate-500 mt-1">Auto-renews yearly at $49.95. Cancel anytime.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-heading font-semibold text-[#0A192F] mb-4">What's included</h3>
            <ul className="space-y-3">
              {INCLUDES.map((f) => <li key={f} className="flex gap-2 text-sm text-slate-700"><CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> {f}</li>)}
            </ul>
          </div>

          <div className="rounded-2xl border-2 border-amber-500 bg-white p-6 shadow-lg">
            {/* product toggle */}
            <div className="grid grid-cols-2 gap-2 mb-5">
              {products.map((p) => (
                <button key={p.id} onClick={() => { setProductId(p.id); setApplied(null); }} className={`rounded-lg border-2 p-3 text-left ${productId === p.id ? "border-amber-400 bg-amber-50" : "border-slate-200"}`} data-testid={`upgrade-product-${p.id}`}>
                  <div className="text-xs text-slate-500">Annual</div>
                  <div className="font-heading font-bold text-[#0A192F]">${p.amount}<span className="text-xs font-normal text-slate-500">/yr</span></div>
                </button>
              ))}
            </div>

            {/* coupon */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Tag className="h-4 w-4" /> Have an agency code?</label>
              <div className="flex gap-2 mt-1.5">
                <Input value={coupon} onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setApplied(null); }} placeholder="ENTER CODE" className="font-mono uppercase" data-testid="upgrade-coupon-input" />
                <Button variant="outline" onClick={() => applyCoupon()} disabled={checking} data-testid="upgrade-apply-coupon-btn">{checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}</Button>
              </div>
              {applied && applied.free_access && <p className="text-xs text-emerald-600 mt-1.5 font-medium">✓ Full access — no payment needed</p>}
              {applied && !applied.free_access && <p className="text-xs text-emerald-600 mt-1.5 font-medium">✓ {applied.label || "Discount"} applied</p>}
              {applied && applied.already_assigned_to && <p className="text-xs text-amber-600 mt-1" data-testid="upgrade-code-assigned">This code has already been assigned to {applied.already_assigned_to}</p>}
            </div>

            <div className="border-t border-slate-200 pt-4 space-y-2">
              <div className="flex justify-between text-sm text-slate-600"><span>{product?.name}</span><span>${base.toFixed(2)}</span></div>
              {applied && <div className="flex justify-between text-sm text-emerald-600"><span>Discount</span><span>−${applied.discount.toFixed(2)}</span></div>}
              <div className="flex justify-between font-heading font-bold text-lg text-[#0A192F]"><span>Total</span><span data-testid="upgrade-total">${finalAmount.toFixed(2)}</span></div>
            </div>

            <Button className="w-full mt-5 bg-amber-600 hover:bg-amber-700 text-white h-12 text-base" onClick={checkout} disabled={loading} data-testid="checkout-pay-button">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Redirecting…</> : (applied?.free_access ? "Activate free access" : `Subscribe · $${finalAmount.toFixed(2)}/yr`)}
            </Button>
            {!applied?.free_access && <p className="text-xs text-slate-400 mt-3 text-center">Auto-renews at $49.95/year. Cancel anytime.{applied ? " Discount applies to your first year." : ""}</p>}
            <p className="text-xs text-slate-400 mt-3 flex items-center justify-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Secure checkout powered by Stripe</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
